import { ChevronRight } from 'lucide-react'

import { type Ancestor } from '@/modules/hierarchy/api/hierarchy-api'
import { cn } from '@/lib/utils'
import { useLoadMore } from '@/lib/use-load-more'
import { Button } from '@/ui/button'

import { useEntityChildren } from '../../lib/entity-queries'
import { ManagesUsersBadge } from './manages-users-badge'
import { rowClass } from './styles'

// One node of the lazy hierarchy tree. It owns its own children query, gated on
// `isExpanded`, so a level is fetched only when drilled into and never up front.
// A leaf (`hasChildren` false) gets no toggle — the server tells us in advance,
// so we never render a dead expander or probe for children we know aren't there.
//
// `pathChild` maps each path parent to the child on the way to the current
// selection. When this node's path-child sorts past its first loaded page, we
// pin it at the top so the selection is always visible — deduped once paging
// reaches it.
export function EntityTreeNode({
  entityId,
  name,
  hasChildren,
  canManageUsers = false,
  depth,
  parentId,
  expanded,
  selectedId,
  activeId,
  pathChild,
  registerNode,
  disabledId = null,
  blockedRootId = null,
  inBlockedSubtree = false,
}: {
  entityId: string
  name: string
  hasChildren: boolean
  canManageUsers?: boolean
  depth: number
  parentId: string | null
  expanded: Set<string>
  selectedId: string | null
  activeId: string | null
  pathChild: Map<string, Ancestor>
  registerNode: (el: HTMLElement | null) => void
  disabledId?: string | null
  blockedRootId?: string | null
  inBlockedSubtree?: boolean
}) {
  const isExpanded = hasChildren && expanded.has(entityId)
  const children = useEntityChildren(entityId, isExpanded)
  const pager = useLoadMore(children, 'child entities')
  const items = pager.items
  const childIndent = { paddingLeft: `${(depth + 1) * 0.75 + 0.5}rem` }

  const selfBlocked = entityId === blockedRootId || inBlockedSubtree
  const selectDisabled = selfBlocked || entityId === disabledId
  const childrenBlocked = selfBlocked
  const isSelected = entityId === selectedId
  const groupId = `tree-group-${entityId}`

  // Surface the on-path child here if pagination hasn't reached it yet.
  const onPath = pathChild.get(entityId)
  const pinned =
    onPath && !items.some((i) => i.entityId === onPath.entityId) ? onPath : null

  const childProps = {
    depth: depth + 1,
    parentId: entityId,
    expanded,
    selectedId,
    activeId,
    pathChild,
    registerNode,
    disabledId,
    blockedRootId,
    inBlockedSubtree: childrenBlocked,
  }

  return (
    <>
      <div
        ref={registerNode}
        role="treeitem"
        tabIndex={activeId === entityId ? 0 : -1}
        aria-label={name}
        aria-level={depth + 1}
        aria-selected={isSelected}
        aria-expanded={hasChildren ? isExpanded : undefined}
        aria-disabled={selectDisabled || undefined}
        aria-owns={isExpanded && hasChildren ? groupId : undefined}
        data-entity-id={entityId}
        data-parent-id={parentId ?? ''}
        data-name={name}
        data-selected={isSelected}
        className={cn(
          rowClass,
          'aria-disabled:opacity-40',
        )}
        style={{ paddingLeft: `${depth * 0.75}rem` }}
      >
        {hasChildren ? (
          <span
            data-tree-chevron=""
            aria-hidden
            className="text-muted-foreground flex size-5 shrink-0 items-center justify-center"
          >
            <ChevronRight
              className={cn(
                'size-3 transition-transform',
                isExpanded && 'rotate-90',
              )}
            />
          </span>
        ) : (
          <span className="size-5 shrink-0" aria-hidden />
        )}
        <span className="min-w-0 truncate">{name}</span>
        {canManageUsers && <ManagesUsersBadge />}
      </div>

      {isExpanded && hasChildren && (
        <div
          id={groupId}
          role="group"
          aria-busy={children.isFetching || undefined}
          className="flex flex-col gap-0.5"
        >
          {pinned && (
            <EntityTreeNode
              key={pinned.entityId}
              entityId={pinned.entityId}
              name={pinned.name}
              hasChildren={pinned.hasChildren}
              {...childProps}
            />
          )}
          {children.isLoading && (
            <p
              role="status"
              className="text-muted-foreground py-0.5 text-xs"
              style={childIndent}
            >
              Loading…
            </p>
          )}
          {children.isError && (
            <div
              role="alert"
              className="flex items-center gap-2 py-0.5"
              style={childIndent}
            >
              <span className="text-destructive text-xs">
                {children.error.message}
              </span>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => void children.refetch()}
              >
                Retry
              </Button>
            </div>
          )}
          {children.isSuccess && items.length === 0 && !pinned && (
            <p
              role="status"
              className="text-muted-foreground py-0.5 text-xs"
              style={childIndent}
            >
              No children.
            </p>
          )}
          {items.map((child) => (
            <EntityTreeNode
              key={child.entityId}
              entityId={child.entityId}
              name={child.name}
              hasChildren={child.hasChildren}
              canManageUsers={child.canManageUsers}
              {...childProps}
            />
          ))}
          {pager.hasNextPage && (
            <button
              {...pager.buttonProps}
              className="text-muted-foreground hover:text-foreground py-0.5 text-left text-xs disabled:opacity-50"
              style={childIndent}
            >
              {pager.label}
            </button>
          )}
        </div>
      )}
    </>
  )
}
